/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { message, Button, Icon, Row, Typography, Table, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import numeral from 'numeral';
import useAuthContext from '../../contexts/AuthContext';
import { ContentCustom, PageHeaderCustom } from '../../components';
import OrderDetails from './OrderDetails';
import OrderAmount from './OrderAmount';

const { Title } = Typography;
const { Column } = Table;

const ShowOrder = () => {
  const { id } = useParams();
  const [isLoading, setLoading] = useState(false);
  const isTableLoading = false;
  const [invoices, setInvoices] = useState([]);
  const { dispatchAPI } = useAuthContext();
  const { t } = useTranslation();
  const [order, setOrder] = useState({});

  const getOrderInvoices = async (orderNumber) => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/invoices?order_number=${orderNumber}`
      });
      setInvoices(data);
    } catch (e) {
      console.error(e);
    }
  };

  const getOrder = async () => {
    try {
      const result = await dispatchAPI('GET', {
        url: `/orders/${id}?populate=client,supplier,articles,contract,owner`
      });
      setOrder(result.data);
      await getOrderInvoices(result.data.order_number);
      setLoading(false);
    } catch (e) {
      if (e.response) {
        message.error('Wrong username or password.');
      } else if (e.request) {
        message.error('Network error. Please check your Internet connexion.');
      } else {
        message.error('Oops, something bad happens.');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      getOrder();
    }
  }, [id]);

  return (
    <>
      <PageHeaderCustom
        title={t('orders.showOrder.title')}
        extra={
          <div>
            <Link to={`/orders/edit/${id}`}>
              <Button type="primary" icon="edit">
                {t('orders.showOrder.editButton')}
              </Button>
            </Link>
          </div>
        }
      />
      <ContentCustom padding={false}>
        <div
          style={{
            padding: 24,
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            width: '100%'
          }}
        >
          <OrderDetails
            invoices={invoices}
            isLoading={isLoading}
            order={order}
          />
          <Row style={{ marginTop: 50 }}>
            <Title level={4}>{t('orders.showOrder.articlesTitle')}</Title>
            <Skeleton
              active
              title={false}
              loading={isTableLoading}
              paragraph={{
                rows: 8,
                width: [
                  '100%',
                  '100%',
                  '100%',
                  '100%',
                  '100%',
                  '100%',
                  '100%',
                  '100%'
                ]
              }}
            >
              <Table
                dataSource={(order.articles || []).map(
                  ({ _id, ...article }) => ({
                    ...article,
                    key: _id
                  })
                )}
                loading={isTableLoading}
                pagination={false}
                expandIcon={(expandProps) => {
                  if (expandProps.record.description) {
                    if (expandProps.expanded) {
                      return (
                        <Icon
                          onClick={(e) => {
                            expandProps.onExpand(expandProps.record, e);
                          }}
                          role="button"
                          type="minus-square"
                        />
                      );
                    }
                    return (
                      <Icon
                        onClick={(e) => {
                          expandProps.onExpand(expandProps.record, e);
                        }}
                        role="button"
                        type="plus-square"
                      />
                    );
                  }
                  return false;
                }}
                expandedRowRender={(record) =>
                  record.description ? record.description : 'Pas de description'
                }
              >
                <Column
                  title={t('orders.showOrder.articlesTable.label')}
                  dataIndex="catalog_label"
                />
                <Column
                  title={t('orders.showOrder.articlesTable.quantity')}
                  dataIndex="quantity"
                  align="right"
                />
                <Column
                  title={t('orders.showOrder.articlesTable.unitPrice')}
                  dataIndex="unit_price"
                  align="right"
                  render={(price) => `${numeral(price).format('0,0.00')} €`}
                />
                <Column
                  title={t('orders.showOrder.articlesTable.discount')}
                  dataIndex="discount"
                  align="right"
                  render={(discount) => (discount ? `${discount}%` : 'N/A')}
                />
                <Column
                  title={t('orders.showOrder.articlesTable.VAT')}
                  dataIndex="VAT"
                  align="right"
                  render={(vat) => `${vat}%`}
                />
                <Column
                  title={t('orders.showOrder.articlesTable.subTotal')}
                  key="subTotal"
                  align="right"
                  render={({ quantity, unit_price }) =>
                    `${numeral(quantity * unit_price).format('0,0.00')} €`
                  }
                />
              </Table>
            </Skeleton>
            <OrderAmount
              isLoading={isLoading}
              isTableLoading={isTableLoading}
              order={order}
            />
          </Row>
        </div>
      </ContentCustom>
    </>
  );
};

export default ShowOrder;
